"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { AlertCircleIcon, RefreshCwIcon } from "lucide-react";
import { useState } from "react";

export const STATUS_LABELS: Record<string, string> = {
  PENDING: "Pending",
  PROCESSING: "Processing",
  COMPLETED: "Ready",
  FAILED: "Failed",
};

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  PENDING: "outline",
  PROCESSING: "secondary",
  COMPLETED: "default",
  FAILED: "destructive",
};

export const STAGE_LABELS: Record<string, string> = {
  STARTED: "Starting…",
  AUTHENTICATING: "Auth…",
  ACCESSING_FILE: "Reading…",
  PARSING: "Parsing…",
  CHUNKING: "Chunking…",
  CREATING_EMBEDDINGS: "Embedding…",
  UPDATING_DATABASE: "Saving…",
  COMPLETED: "Ready",
  ERROR: "Error",
};

export function isFileProcessing(status?: string | null): boolean {
  const s = status?.toUpperCase();
  return s === "PENDING" || s === "PROCESSING";
}

export function FileStatusBadge({
  status,
  stage,
}: {
  status?: string | null;
  stage?: string | null;
}) {
  const upperStatus = status?.toUpperCase();
  const upperStage = stage?.toUpperCase();
  if (!upperStatus) return null;

  // While actively processing, show the stage label
  const label =
    upperStatus === "PROCESSING" && upperStage && STAGE_LABELS[upperStage]
      ? STAGE_LABELS[upperStage]
      : (STATUS_LABELS[upperStatus] ?? upperStatus);

  return (
    <Badge variant={STATUS_VARIANTS[upperStatus] ?? "outline"} className="whitespace-nowrap text-xs">
      {label}
    </Badge>
  );
}

// Compact icon-only status indicator: a tick when ready, a spinner while
// processing, and a clickable disclaimer (pending/failed) that pops open an
// explanation with a reprocess action — for tight layouts like file cards.
export function FileStatusIcon({
  status,
  stage,
  error,
  onReprocess,
}: {
  status?: string | null;
  stage?: string | null;
  error?: string | null;
  onReprocess?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const upperStatus = status?.toUpperCase();
  const upperStage = stage?.toUpperCase();
  if (!upperStatus) return null;

  if (upperStatus === "COMPLETED") {
    return null;
  }

  if (upperStatus === "PROCESSING") {
    const stageLabel = upperStage ? STAGE_LABELS[upperStage] : undefined;
    return (
      <span title={stageLabel ?? "Processing"}>
        <Spinner className="size-3.5 text-muted-foreground" />
      </span>
    );
  }

  const isFailed = upperStatus === "FAILED";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={isFailed ? "Failed — click for details" : "Pending — click for details"}
          className={isFailed ? "text-destructive" : "text-muted-foreground transition-colors hover:text-foreground"}
          onClick={(e) => e.stopPropagation()}
        >
          <AlertCircleIcon size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end" onClick={(e) => e.stopPropagation()}>
        <p className="text-sm text-foreground">
          {isFailed
            ? (error ?? "Something went wrong while processing this file.")
            : "This file hasn't started processing yet."}
        </p>
        {onReprocess && (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onReprocess();
              setOpen(false);
            }}
          >
            <RefreshCwIcon className="size-3.5" />
            Reprocess
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}
